import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RosterEntry } from '../api/characters';
import { useAuth } from '../services/AuthContext';
import { PresenceEvent, usePresenceStream } from './usePresenceStream';

/** Keeps every cached view of your characters in step with the presence stream,
 * on every page. Mounted once in the layout. The stream used to be subscribed
 * from the dashboard alone, so a move while you were on a system page updated
 * nothing until you navigated back. */
export function usePresenceSync(): void {
  const queryClient = useQueryClient();
  const { me } = useAuth();

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['characters', 'roster'] });
    void queryClient.invalidateQueries({ queryKey: ['systems', 'favorites'] });
    void queryClient.invalidateQueries({ queryKey: ['maps'] });
    // System pages and the current-system intel read the character's location
    // too, so they refresh with it instead of holding the answer they loaded.
    void queryClient.invalidateQueries({ queryKey: ['system-by-name'] });
    void queryClient.invalidateQueries({ queryKey: ['intel'] });
  }, [queryClient]);

  const onEvent = useCallback(
    async (event: PresenceEvent) => {
      // Apply the observed location immediately, then reconcile everything else.
      await queryClient.cancelQueries({ queryKey: ['characters', 'roster'] });
      queryClient.setQueryData<RosterEntry[]>(['characters', 'roster'], (current) =>
        current?.map((row) =>
          row.eveCharacterId === event.eveCharacterId
            ? { ...row, solarSystemId: event.solarSystemId, systemName: event.systemName, systemClass: event.systemClass }
            : row,
        ),
      );
      refresh();
    },
    [queryClient, refresh],
  );

  usePresenceStream(onEvent, me.isMember, refresh);
}
